/**
 * dashboardWidgetWalk.ts
 *
 * Path-addressable widget walking for #969 Part F (Review Changes panel) and
 * Part E (broken table reference). The Review Changes panel needs a stable
 * way to say "this change belongs to that widget" and to jump the viewer to
 * it — table_graph widgets carry no id of their own (unlike
 * multi_table_analysis, which has `multi_table_id`), so position is the only
 * available key.
 *
 * The path scheme is `${pageIndex}-${rowIndex}-${cellIndex}`, with a
 * `-${innerRowIndex}-${innerCellIndex}` pair appended per nesting level for a
 * widget inside a "layout" cell. `pageIndex` is 0 for a legacy
 * (pages-less) dashboard, matching DashboardRowComponent's existing
 * `row-${currentPageIndex}-${rowIndex}` DOM id convention — see
 * components/dashboard/DashboardCellComponent.tsx, which mirrors this same
 * path into a `cell-${path}` DOM id for scroll-to-widget.
 *
 * This is a read-only, purely positional address. It is NOT the
 * "P{n}/r{i}/c{j}" path goapi/pkg/dashboardcards/dashboardcards.go uses
 * internally for multi_table_analysis cells — that scheme never leaves Go,
 * and table_graph cells have no Go-side equivalent to key against. Kept
 * separate rather than unified so a future change to one does not silently
 * break the other.
 */

import { DashboardConfig, DashboardLayout, DashboardCell, TableGraphWidgetData, MultiTableAnalysisWidgetData } from '../types/gotypes'

export interface WidgetRef<T> {
  /** `${pageIndex}-${rowIndex}-${cellIndex}[-${innerRowIndex}-${innerCellIndex}]*` */
  path: string
  pageIndex: number
  data: T
}

function walkLayout<T>(
  layout: DashboardLayout | undefined,
  pageIndex: number,
  prefix: string,
  pick: (cell: DashboardCell) => T | undefined,
  out: WidgetRef<T>[],
): void {
  if (!layout?.rows) return
  layout.rows.forEach((row, rowIndex) => {
    (row.cells || []).forEach((cell, cellIndex) => {
      const path = `${prefix}${rowIndex}-${cellIndex}`
      const data = pick(cell)
      if (data !== undefined) {
        out.push({ path, pageIndex, data })
      }
      if (cell.widget_type === 'layout' && cell.layout_data) {
        walkLayout(cell.layout_data, pageIndex, `${path}-`, pick, out)
      }
      // A report pillar (#1080) projects entry k onto inner row k, cell 0 —
      // the same address the nested layout it replaced produced, so converted
      // dashboards keep every widget path they had.
      if (cell.widget_type === 'report_pillar' && cell.report_pillar_data) {
        walkLayout(
          { rows: cell.report_pillar_data.entries.map(entry => ({ cells: [entry.cell] })) },
          pageIndex, `${path}-`, pick, out,
        )
      }
    })
  })
}

/** Every layout in the dashboard, paired with the page index that owns it
 *  (0 for a legacy pages-less dashboard, matching the DOM id convention). */
function layoutsByPage(config: DashboardConfig): { pageIndex: number; layout: DashboardLayout | undefined }[] {
  const pages = config.pages || []
  if (pages.length > 0) return pages.map((page, i) => ({ pageIndex: i, layout: page.layout }))
  return [{ pageIndex: 0, layout: config.layout }]
}

function walkWidgets<T>(config: DashboardConfig, pick: (cell: DashboardCell) => T | undefined): WidgetRef<T>[] {
  const out: WidgetRef<T>[] = []
  layoutsByPage(config).forEach(({ pageIndex, layout }) => {
    walkLayout(layout, pageIndex, '', pick, out)
  })
  return out
}

/** Every table_graph widget in the dashboard, addressed by path. */
export function walkTableGraphWidgets(config: DashboardConfig): WidgetRef<TableGraphWidgetData>[] {
  return walkWidgets(config, cell => cell.widget_type === 'table_graph' ? cell.table_graph_data : undefined)
}

/** Every multi_table_analysis widget in the dashboard, addressed by path. */
export function walkMultiTableWidgets(config: DashboardConfig): WidgetRef<MultiTableAnalysisWidgetData>[] {
  return walkWidgets(config, cell => cell.widget_type === 'multi_table_analysis' ? cell.multi_table_analysis_data : undefined)
}

/** The DOM id a widget at `path` is rendered with — see DashboardCellComponent.tsx. */
export function widgetDomId(path: string): string {
  return `cell-${path}`
}
